import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Trash2, FileText, ExternalLink } from "lucide-react";
import { adminApi, AdminHeader } from "@/lib/adminApi";
import { UploadButton } from "@/components/admin/Uploads";

const field = "w-full bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-violet)]";
const box = "bg-[var(--bg-card)] border-glow rounded-xl p-6";

const PhotoList = ({ title, items, onChange, textKey, placeholder, testId, withKind }) => {
  const set = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className={box}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-semibold">{title}</h2>
        <UploadButton label="Add photo" testId={`${testId}-add`} onUploaded={(url) => onChange([...items, { url, [textKey]: "", ...(withKind ? { kind: "portrait" } : {}) }])} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <div key={`${it.url}-${i}`} className="bg-[var(--bg-secondary)] rounded-lg overflow-hidden" data-testid={`${testId}-item-${i}`}>
            <img src={it.url} alt="" className="w-full aspect-video object-cover" />
            <div className="p-3 space-y-2">
              <input value={it[textKey] || ""} onChange={(e) => set(i, { [textKey]: e.target.value })} placeholder={placeholder} className={field} data-testid={`${testId}-text-${i}`} />
              <div className="flex items-center gap-2">
                {withKind && (
                  <select value={it.kind || "portrait"} onChange={(e) => set(i, { kind: e.target.value })} className={field} data-testid={`${testId}-kind-${i}`}>
                    <option value="stage">Stage</option><option value="portrait">Portrait</option>
                  </select>
                )}
                <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-[var(--text-secondary)] hover:text-red-400 ml-auto" data-testid={`${testId}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminMedia = () => {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi("/media").then(setKit).catch((e) => toast.error(e.message)); }, []);
  if (!kit) return <div className="text-[var(--text-secondary)]">Loading...</div>;

  const up = (k, v) => setKit((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await adminApi("/admin/media", { method: "PUT", body: kit }); toast.success("Media kit saved"); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl space-y-6">
      <AdminHeader title="Media Kit" subtitle="Bios, photos, headshots and one-sheet shown on the public Media page" testId="media-admin-heading">
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60" data-testid="media-save-btn"><Save className="w-4 h-4" /> Save changes</button>
      </AdminHeader>

      <div className={`${box} space-y-4`}>
        <h2 className="text-white font-semibold">Press Bios</h2>
        {["short", "medium", "long"].map((k) => (
          <div key={k}>
            <label className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-1.5 block">{k} bio</label>
            <textarea value={kit.bios[k] || ""} onChange={(e) => up("bios", { ...kit.bios, [k]: e.target.value })} rows={k === "long" ? 8 : 3} className={`${field} resize-y`} data-testid={`bio-${k}-input`} />
          </div>
        ))}
      </div>

      <PhotoList title="Photo Gallery" items={kit.gallery} onChange={(v) => up("gallery", v)} textKey="caption" placeholder="Caption" testId="gallery" withKind />
      <PhotoList title="Downloadable Headshots" items={kit.headshots} onChange={(v) => up("headshots", v)} textKey="label" placeholder="Label (e.g. Executive portrait)" testId="headshots" />

      <div className={box}>
        <h2 className="text-white font-semibold mb-1">Speaker One-Sheet (PDF)</h2>
        <p className="text-xs text-[var(--text-secondary)] mb-4">Upload your designed PDF. If none is uploaded, the Media page offers an auto-generated printable one-sheet.</p>
        <div className="flex items-center gap-3">
          {kit.one_sheet_url ? (
            <a href={kit.one_sheet_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[var(--accent-violet-light)]" data-testid="one-sheet-current"><FileText className="w-4 h-4" /> Current one-sheet <ExternalLink className="w-3 h-3" /></a>
          ) : <span className="text-sm text-[var(--text-secondary)]" data-testid="one-sheet-none">Using auto-generated one-sheet</span>}
          <UploadButton accept="application/pdf" label={kit.one_sheet_url ? "Replace PDF" : "Upload PDF"} testId="one-sheet-upload" onUploaded={(url) => up("one_sheet_url", url)} />
          {kit.one_sheet_url && <button onClick={() => up("one_sheet_url", "")} className="text-xs text-red-400" data-testid="one-sheet-remove">Remove</button>}
        </div>
      </div>
    </div>
  );
};

export default AdminMedia;
